import { z } from "zod/v4";
import {
  clipLeadGenAdLineToWidth,
  leadGenAdOverlayBulletFits,
  leadGenAdOverlayTitleFits,
} from "./lead-gen-ad-measure";
import {
  LEAD_GEN_INDUSTRY_GROUPS,
  getLeadGenIndustryGroup,
  getLeadGenIndustryGroupByIntakeKey,
} from "./lead-gen-industries";

/**
 * Lead Generation campaign focus: the broker's lane (deal size, industry, or
 * general), the industries they want, the revenue band they serve, and the
 * proof points that make them credible. Shared by the wizard, prompts,
 * targeting, intake qualification, snapshots, and playbooks.
 */

export const LEAD_GEN_LANE_TYPES = ["DEAL_SIZE", "INDUSTRY", "GENERAL"] as const;
export type LeadGenLaneType = (typeof LEAD_GEN_LANE_TYPES)[number];

export const LEAD_GEN_LANE_LABELS: Record<LeadGenLaneType, string> = {
  DEAL_SIZE: "Established businesses by size",
  INDUSTRY: "Specific industries",
  GENERAL: "Any business owner",
};

/** Revenue band presets in cents. */
export const LEAD_GEN_REVENUE_BAND_PRESETS_CENTS = [
  500_000_00, 1_000_000_00, 3_000_000_00, 5_000_000_00, 10_000_000_00,
  25_000_000_00, 50_000_000_00,
] as const;

export function formatLeadGenRevenue(cents: number): string {
  const dollars = cents / 100;
  if (dollars >= 1_000_000) {
    const millions = dollars / 1_000_000;
    return `$${Number.isInteger(millions) ? millions : millions.toFixed(1)}M`;
  }
  if (dollars >= 1_000) return `$${Math.round(dollars / 1_000)}K`;
  return `$${Math.round(dollars)}`;
}

export const LEAD_GEN_PROOF_POINT_MAX = 6;
/** Short facts the ads can draw from. Not the final overlay lines. */
export const LEAD_GEN_PROOF_POINT_MAX_LENGTH = 48;
/** Tone brief for the copywriter. Not overlay copy. */
export const LEAD_GEN_POSITIONING_MAX_LENGTH = 140;
/** Optional notes the broker tells Venturu AI. Not overlay copy. */
export const LEAD_GEN_ADDITIONAL_INSTRUCTIONS_MAX_LENGTH = 2000;
/**
 * Character ceiling for one line on the 1080 creative info card.
 * Width is enforced separately: a 32-character title-case line can still
 * run off the card. See `lead-gen-ad-measure.ts`.
 */
export const LEAD_GEN_AD_OVERLAY_BULLET_MAX_LENGTH = 32;
export const LEAD_GEN_AD_OVERLAY_TITLE_MAX_LENGTH = 28;
export const LEAD_GEN_AD_HEADLINE_MAX_LENGTH = 40;
/**
 * First-pass generation may run a little long. A rewrite loop shortens
 * copy down to the display max instead of failing the campaign.
 */
export const LEAD_GEN_AD_OVERLAY_BULLET_GENERATE_MAX_LENGTH = 64;
export const LEAD_GEN_AD_OVERLAY_TITLE_GENERATE_MAX_LENGTH = 48;
export const LEAD_GEN_AD_HEADLINE_GENERATE_MAX_LENGTH = 80;

function clipToLeadGenAdLength(value: string, max: number): string {
  const trimmed = value.trim();
  if (trimmed.length <= max) return trimmed;
  const sliced = trimmed.slice(0, max);
  const lastSpace = sliced.lastIndexOf(" ");
  const cut =
    lastSpace >= Math.floor(max * 0.6) ? sliced.slice(0, lastSpace) : sliced;
  return cut.replace(/[\s.,;:–—-]+$/u, "").trim();
}

/**
 * Lead-ad titles are customer-facing copy. Expand common title abbreviations
 * before rendering or fitting so generated text never displays "Bus. Broker".
 */
export function expandLeadGenAdTitleAbbreviations(value: string): string {
  return value
    .replace(/\bBus\.\s*(?=Broker\b)/giu, "Business ")
    .replace(/\bBiz\.\s*(?=Broker\b)/giu, "Business ")
    .replace(/\bProf\.\s*(?=[A-Z])/gu, "Professional ");
}

export function clipLeadGenAdOverlayTitle(value: string): string {
  const expanded = expandLeadGenAdTitleAbbreviations(value);
  if (
    expanded !== value &&
    expanded.length > LEAD_GEN_AD_OVERLAY_TITLE_MAX_LENGTH &&
    /\bBusiness Broker\b/u.test(expanded)
  ) {
    const shorter = expanded.replace(/\bBusiness Broker\b/u, "Broker");
    if (shorter.length <= LEAD_GEN_AD_OVERLAY_TITLE_MAX_LENGTH) return shorter;
  }
  return clipLeadGenAdLineToWidth(
    clipToLeadGenAdLength(expanded, LEAD_GEN_AD_OVERLAY_TITLE_MAX_LENGTH),
    leadGenAdOverlayTitleFits,
  );
}

export function clipLeadGenAdOverlayBullet(value: string): string {
  return clipLeadGenAdLineToWidth(
    clipToLeadGenAdLength(value, LEAD_GEN_AD_OVERLAY_BULLET_MAX_LENGTH),
    leadGenAdOverlayBulletFits,
  );
}

export function clipLeadGenAdHeadline(value: string): string {
  return clipToLeadGenAdLength(value, LEAD_GEN_AD_HEADLINE_MAX_LENGTH);
}

export const BrokerLeadCampaignFocusSchema = z
  .object({
    laneType: z.enum(LEAD_GEN_LANE_TYPES).default("GENERAL"),
    industries: z
      .array(z.string().trim().min(1))
      .max(LEAD_GEN_INDUSTRY_GROUPS.length)
      .default([]),
    industryExclusions: z
      .array(z.string().trim().min(1))
      .max(LEAD_GEN_INDUSTRY_GROUPS.length)
      .default([]),
    revenueFloorCents: z.number().int().nonnegative().nullable().default(null),
    revenueCeilingCents: z.number().int().positive().nullable().default(null),
    proofPoints: z
      .array(z.string().trim().min(2).max(LEAD_GEN_PROOF_POINT_MAX_LENGTH))
      .max(LEAD_GEN_PROOF_POINT_MAX)
      .default([]),
    positioningNote: z.string().trim().max(LEAD_GEN_POSITIONING_MAX_LENGTH).nullable().default(null),
  })
  .superRefine((value, ctx) => {
    for (const key of [...value.industries, ...value.industryExclusions]) {
      if (!Boolean(getLeadGenIndustryGroup(key))) {
        ctx.addIssue({
          code: "custom",
          message: `Unknown industry group: ${key}`,
          path: ["industries"],
        });
      }
    }
    if (
      value.revenueFloorCents != null &&
      value.revenueCeilingCents != null &&
      value.revenueCeilingCents <= value.revenueFloorCents
    ) {
      ctx.addIssue({
        code: "custom",
        message: "Revenue ceiling must be above the floor",
        path: ["revenueCeilingCents"],
      });
    }
  });

export type BrokerLeadCampaignFocus = z.infer<typeof BrokerLeadCampaignFocusSchema>;

export const DEFAULT_LEAD_GEN_FOCUS: BrokerLeadCampaignFocus = {
  laneType: "GENERAL",
  industries: [],
  industryExclusions: [],
  revenueFloorCents: null,
  revenueCeilingCents: null,
  proofPoints: [],
  positioningNote: null,
};

/** Derive the lane from the concrete selections so UI and prompts agree. */
export function deriveLeadGenLaneType(input: {
  industries: readonly string[];
  revenueFloorCents: number | null;
}): LeadGenLaneType {
  const hasIndustries = input.industries.some((key) => key !== "other");
  const hasFloor = (input.revenueFloorCents ?? 0) > 0;
  if (hasIndustries) return "INDUSTRY";
  if (hasFloor) return "DEAL_SIZE";
  return "GENERAL";
}

/** Accept loosely-typed persisted data (Prisma columns or JSON) and normalize. */
export function parseLeadGenFocus(value: unknown): BrokerLeadCampaignFocus {
  const parsed = BrokerLeadCampaignFocusSchema.safeParse(clipFocusCopy(value ?? {}));
  if (!parsed.success) return { ...DEFAULT_LEAD_GEN_FOCUS };
  return parsed.data;
}

function clipFocusCopy(value: unknown): unknown {
  if (!value || typeof value !== "object") return value;
  const row = value as Record<string, unknown>;
  return {
    ...row,
    proofPoints: Array.isArray(row.proofPoints)
      ? row.proofPoints.map((point) =>
          typeof point === "string"
            ? point.slice(0, LEAD_GEN_PROOF_POINT_MAX_LENGTH)
            : point,
        )
      : row.proofPoints,
    positioningNote:
      typeof row.positioningNote === "string"
        ? row.positioningNote.slice(0, LEAD_GEN_POSITIONING_MAX_LENGTH)
        : row.positioningNote,
  };
}

export function leadGenFocusFromCampaign(campaign: {
  laneType?: string | null;
  focusIndustries?: string[] | null;
  focusIndustryExclusions?: string[] | null;
  revenueFloorCents?: number | null;
  revenueCeilingCents?: number | null;
  proofPoints?: string[] | null;
  positioningNote?: string | null;
}): BrokerLeadCampaignFocus {
  return parseLeadGenFocus({
    laneType: campaign.laneType ?? "GENERAL",
    industries: campaign.focusIndustries ?? [],
    industryExclusions: campaign.focusIndustryExclusions ?? [],
    revenueFloorCents: campaign.revenueFloorCents ?? null,
    revenueCeilingCents: campaign.revenueCeilingCents ?? null,
    proofPoints: campaign.proofPoints ?? [],
    positioningNote: campaign.positioningNote ?? null,
  });
}

export function leadGenFocusToCampaignData(focus: BrokerLeadCampaignFocus) {
  return {
    laneType: focus.laneType,
    focusIndustries: focus.industries,
    focusIndustryExclusions: focus.industryExclusions,
    revenueFloorCents: focus.revenueFloorCents,
    revenueCeilingCents: focus.revenueCeilingCents,
    proofPoints: focus.proofPoints,
    positioningNote: focus.positioningNote,
  };
}

export function describeLeadGenRevenueBand(focus: {
  revenueFloorCents: number | null;
  revenueCeilingCents: number | null;
}): string | null {
  const { revenueFloorCents: floor, revenueCeilingCents: ceiling } = focus;
  if (floor && ceiling) {
    return `${formatLeadGenRevenue(floor)}–${formatLeadGenRevenue(ceiling)} in annual revenue`;
  }
  if (floor) return `${formatLeadGenRevenue(floor)}+ in annual revenue`;
  if (ceiling) return `under ${formatLeadGenRevenue(ceiling)} in annual revenue`;
  return null;
}

export function describeLeadGenIndustries(keys: readonly string[]): string | null {
  const labels = keys
    .map((key) => getLeadGenIndustryGroup(key)?.label)
    .filter((label): label is string => Boolean(label));
  if (labels.length === 0) return null;
  if (labels.length <= 2) return labels.join(" and ");
  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
}

/** Stable key for the audience slice (lane, industries, revenue). Copy is excluded. */
export function leadGenFocusAudienceKey(
  focus: Pick<
    BrokerLeadCampaignFocus,
    | "laneType"
    | "industries"
    | "industryExclusions"
    | "revenueFloorCents"
    | "revenueCeilingCents"
  >,
): string {
  return [
    focus.laneType,
    [...focus.industries].sort().join(","),
    [...focus.industryExclusions].sort().join(","),
    focus.revenueFloorCents ?? "",
    focus.revenueCeilingCents ?? "",
  ].join("|");
}

/** One-paragraph description for prompts, UI summaries, and agent answers. */
export function describeLeadGenFocus(focus: BrokerLeadCampaignFocus): string {
  const parts: string[] = [];
  const industries = describeLeadGenIndustries(focus.industries);
  const band = describeLeadGenRevenueBand(focus);
  switch (focus.laneType) {
    case "DEAL_SIZE":
      parts.push(
        `Deal-size lane: established businesses${band ? ` with ${band}` : ""}, any industry${
          industries ? ` (leaning ${industries})` : ""
        }.`,
      );
      break;
    case "INDUSTRY":
      parts.push(
        `Industry lane: ${industries ?? "selected industries"}${band ? ` with ${band}` : ""}.`,
      );
      break;
    default:
      parts.push(
        `General lane: business owners considering a sale${band ? ` with ${band}` : ""}.`,
      );
  }
  const exclusions = describeLeadGenIndustries(focus.industryExclusions);
  if (exclusions) parts.push(`Not a fit: ${exclusions}.`);
  if (focus.proofPoints.length) {
    parts.push(`Proof points: ${focus.proofPoints.join("; ")}.`);
  }
  if (focus.positioningNote) parts.push(`Positioning: ${focus.positioningNote}`);
  return parts.join(" ");
}

/** Whether the lane tolerates limited financial vocabulary (still no jargon dumps). */
export function leadGenFocusAllowsFinancialVocabulary(
  focus: BrokerLeadCampaignFocus,
): boolean {
  return (
    focus.laneType === "DEAL_SIZE" &&
    (focus.revenueFloorCents ?? 0) >= 3_000_000_00
  );
}

export type LeadGenFocusMismatchReason =
  | "BELOW_REVENUE_FLOOR"
  | "ABOVE_REVENUE_CEILING"
  | "OUTSIDE_INDUSTRY"
  | "EXCLUDED_INDUSTRY";

/**
 * Score an intake answer set against the focus. Unknown answers never count
 * against the lead; only explicit mismatches do.
 */
export function evaluateLeadGenFocusMatch(input: {
  focus: BrokerLeadCampaignFocus;
  /** Legacy lead-form industry option key. */
  intakeIndustryKey?: string | null;
  /** Already-resolved lead-gen industry group key (e.g. from marketplace business type). */
  industryGroupKey?: string | null;
  intakeRevenue?: { minCents: number | null; maxCents: number | null } | null;
}): { inFocus: boolean | null; reasons: LeadGenFocusMismatchReason[] } {
  const reasons: LeadGenFocusMismatchReason[] = [];
  let evaluated = false;
  const { focus } = input;

  const group = input.industryGroupKey
    ? getLeadGenIndustryGroup(input.industryGroupKey)
    : input.intakeIndustryKey
      ? getLeadGenIndustryGroupByIntakeKey(input.intakeIndustryKey)
      : undefined;
  if (group) {
    if (group.key !== "other") {
      evaluated = true;
      if (focus.industryExclusions.includes(group.key)) {
        reasons.push("EXCLUDED_INDUSTRY");
      } else if (
        focus.laneType === "INDUSTRY" &&
        focus.industries.length > 0 &&
        !focus.industries.includes(group.key)
      ) {
        reasons.push("OUTSIDE_INDUSTRY");
      }
    }
  }

  const revenue = input.intakeRevenue;
  if (revenue && (revenue.minCents != null || revenue.maxCents != null)) {
    evaluated = true;
    if (
      focus.revenueFloorCents != null &&
      revenue.maxCents != null &&
      revenue.maxCents <= focus.revenueFloorCents
    ) {
      reasons.push("BELOW_REVENUE_FLOOR");
    }
    if (
      focus.revenueCeilingCents != null &&
      revenue.minCents != null &&
      revenue.minCents >= focus.revenueCeilingCents
    ) {
      reasons.push("ABOVE_REVENUE_CEILING");
    }
  }

  if (!evaluated) return { inFocus: null, reasons: [] };
  return { inFocus: reasons.length === 0, reasons };
}
